/**
 * =============================================================================
 * IDS CRM Web - Engineer Today Screen (Batch D)
 * =============================================================================
 *
 * One mobile-first view of the engineer's day: clock state + hours, today's
 * scheduled jobs, open tickets, tonight's standby and any active absence.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { readLocationStatus, locationStatusLabel, getDeviceLocation } from '../utils/geolocation';
import { getCapabilities, canFitParts } from '../utils/permissions';
import CoordsCopy from '../components/CoordsCopy';
import { formatDate, formatDateTime, formatTime } from '../utils/format';
import { toDateKey, startOfWeek, formatDateKey, ukTodayKey, nightsPhrase } from '../utils/dates';
import { getLogWorkPhoneStore } from '../utils/logWorkPhoneStore';
import useSavedLogWork, { NO_SIGNAL_SAVE_MESSAGE, SEND_FAILED_MESSAGE } from '../hooks/useSavedLogWork';
import { buildLogWorkPayload } from '../utils/logWorkPayload';
import { emptyDailyVanCheck, validateDailyVanCheck } from '../components/DailyVanCheckSection';
import { outcomeClosesWork } from '../components/WorkLogOutcomeSection';
import { ARRIVED_HOME_LABEL } from '../components/JobStagesCard';
import { attachmentFromFileRef } from '../hooks/useTemplateCheckForm';
import { spareValueProblem } from '../utils/spareLines';
import { useSparePartsList } from '../utils/sparePartsList';
import './Today.css';

function formatHours(ms) {
  return `${((ms || 0) / 3600000).toFixed(1)}h`;
}

// UK days written out by hand: en-GB Intl prints September as "Sept".
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The UK day of an instant, 'YYYY-MM-DD' ('' when it cannot be read). */
function ukKeyOf(value) {
  const instant = value instanceof Date ? value : new Date(value);
  return Number.isNaN(instant.getTime()) ? '' : ukTodayKey(instant);
}

/** 'Mon 21 Sep' for a 'YYYY-MM-DD' key. */
function ukDayName(key) {
  const [year, month, day] = key.split('-').map(Number);
  return `${WEEKDAYS[new Date(Date.UTC(year, month - 1, day, 12)).getUTCDay()]} ${day} ${SHORT_MONTHS[month - 1]}`;
}

/** The key of the day before a 'YYYY-MM-DD' key. */
function dayBefore(key) {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day - 1, 12)).toISOString().slice(0, 10);
}

/** 'saved 16:05' today, 'saved yesterday 16:05', 'saved Mon 21 Sep 16:05' before that (UK time). */
function savedWhen(value, now = new Date()) {
  const key = ukKeyOf(value);
  if (!key) return '';
  const today = ukTodayKey(now);
  const clock = formatTime(value);
  if (key === today) return `saved ${clock}`;
  if (key === dayBefore(today)) return `saved yesterday ${clock}`;
  return `saved ${ukDayName(key)} ${clock}`;
}

/**
 * Today's "Log Work to finish" rows (Phase 6 step 7): the saved Log Work the
 * server lists for this engineer (log_work_to_finish), merged with the jobs
 * that have changes on this phone not yet sent (logWorkPhoneStore). A job in
 * both is one row.
 */
function mergeLogWorkToFinish(serverRows, phoneRows, tasksToday) {
  const rows = [];
  const byTask = new Map();
  (Array.isArray(serverRows) ? serverRows : []).forEach(row => {
    if (!row || !row.task_id) return;
    const merged = { ...row, phone: null };
    byTask.set(String(row.task_id), merged);
    rows.push(merged);
  });
  (Array.isArray(phoneRows) ? phoneRows : []).forEach(entry => {
    if (!entry || !entry.taskId) return;
    const existing = byTask.get(String(entry.taskId));
    if (existing) {
      existing.phone = entry;
      return;
    }
    const task = (tasksToday || []).find(item => String(item.task_id) === String(entry.taskId)) || {};
    const merged = {
      task_id: entry.taskId,
      task_ref: task.task_ref || null,
      title: task.title || null,
      customer_name: task.customer_name || null,
      phone_only: true,
      phone: entry
    };
    byTask.set(String(entry.taskId), merged);
    rows.push(merged);
  });
  return rows;
}

function logWorkRowDetail(row) {
  if (row.phone_only) return null;
  if (row.carried_untouched && row.carried_from_saved_at) {
    return `carries on from ${ukDayName(ukKeyOf(row.carried_from_saved_at))} - start today's Job Stages here`;
  }
  const parts = [];
  const when = row.last_changed_at ? savedWhen(row.last_changed_at) : '';
  if (when) parts.push(`${when}${row.last_changed_by_name ? ` by ${row.last_changed_by_name}` : ''}`);
  if (Number(row.robots_total) > 0) parts.push(`${Number(row.robots_done) || 0} of ${Number(row.robots_total)} robots done`);
  return parts.join(' · ');
}

function phoneLine(entry) {
  if (!entry) return null;
  if (entry.staleDraft || Number(entry.stale) > 0) return 'Changes on this phone were not filed - open to check';
  return 'Changes on this phone not sent yet';
}

// ---------------------------------------------------------------------------
// 7c: one-tap Arrived home for a job whose saved Log Work is ready to close
// ---------------------------------------------------------------------------

export const JOB_CLOSE_OPEN_MESSAGE = 'Open the job to finish Log Work there.';
export const ARRIVED_HOME_LOST_MESSAGE = `No signal - we could not tell if ${ARRIVED_HOME_LABEL} was recorded. Tap again when you have signal; it will not be recorded twice.`;
export const ARRIVED_HOME_CHECK_MESSAGE = `No signal - ${ARRIVED_HOME_LABEL} was not recorded. Tap again when you have signal.`;

/**
 * Reads my Job Stages record: true when Arrived home is already set on it
 * (a stamp whose reply was lost landed). Throws with a plain sentence when
 * there is no answer, so nothing is stamped blind.
 */
async function arrivalAlreadyRecorded(authFetch, travelId) {
  let response;
  try {
    response = await authFetch(`/api/v1/travel/${encodeURIComponent(travelId)}`);
  } catch (err) {
    throw new Error(ARRIVED_HOME_CHECK_MESSAGE);
  }
  if (!response || !response.ok) {
    if (response && response.status >= 400 && response.status < 500) return false;
    throw new Error(ARRIVED_HOME_CHECK_MESSAGE);
  }
  const data = await response.json().catch(() => null);
  const record = data && data.travel_id ? data : (data && data.data && data.data.travel_id ? data.data : null);
  return Boolean(record && record.returned_at);
}

export const JOB_CLOSE_CHANGED_MESSAGE ='This Log Work changed and is no longer ready to save from here. Open the job to check it, then save.';

const lowerId = (value) => String(value ?? '').trim().toLowerCase();
const listLength = (list) => (Array.isArray(list) ? list.length : 0);
// A reopened work log, or a request to reopen one: straight to the Work Logs tab it is on.
const reopenedWorkHref = (entry) => (entry.task_id ? `/tasks/${entry.task_id}?tab=work` : `/tickets/${entry.ticket_id}?tab=work`);

/**
 * Only the saved Log Work rows worth asking the server about: my own Job
 * Stages are in it, it is not a carried day nobody has touched, and every
 * robot on the job is already signed off.
 */
function jobCloseCandidates(rows) {
  return (Array.isArray(rows) ? rows : []).filter(row => row && row.task_id && row.draft_id
    && row.my_unfiled_journey === true && !row.carried_untouched
    && Number(row.robots_total) > 0 && Number(row.robots_done) === Number(row.robots_total));
}

/**
 * Whether a saved Log Work can be saved from Today with one tap, and what the
 * Log Work window would send if it were opened on it (spec 6.4 restore order).
 * Null unless ALL of these hold, so anything unusual goes through the job page:
 *  - loaded, with no conflict, stale copy, failed item or photo still sending;
 *  - the outcome part closes the job (not Carry on tomorrow), as site work;
 *  - my Job Stages part: Finished site work, no Arrived home (unless this
 *    tap stamped it), not linked, and started on today's UK day (an earlier
 *    day needs the job page's "I did not work on this job today" tick);
 *  - every robot on the job has its own part: Not done with a reason, or Done
 *    with a completed form for this work type; no job-level (sales) form;
 *  - every Parts used line would be filed: a robot (or Whole job) on a job
 *    with several, a robot still on the job, a quantity that can be read;
 *  - a crew part, or a booking with nobody else on it (the job page fills the
 *    crew from the booking only when there is no crew part);
 *  - the van check passes the window's own validation, seats only the crew,
 *    and a second van names its van and driver.
 *
 * @returns {null | { formData, sections, followUpPerRobot, journey, crewIds, vanCheck, secondVan, secondVanCheckOpen, secondVanCheck, workPeriod, robotsTotal }}
 */
export function planJobClose(saved, { userId, config, soloBooking = false, arrivedHere = false, now = new Date() } = {}) {
  if (!saved || saved.status !== 'ready' || !saved.draftId || !config) return null;
  if (listLength(saved.conflicts) || listLength(saved.staleCopies) || listLength(saved.failed)
    || listLength(saved.pendingPhotos)) return null;
  const me = lowerId(userId);
  const parts = saved.parts || {};
  const valueOf = (key) => (parts[key] && parts[key].value) || null;
  const files = Array.isArray(saved.files) ? saved.files : [];
  const filesOf = (key) => files.filter(file => file && file.part_key === key);

  const outcome = valueOf('outcome');
  if (!me || !outcome || !outcomeClosesWork(config, outcome.outcome) || outcome.assign_next_type === 'carry_on') return null;
  const work = valueOf('work') || {};
  const logType = work.log_type || config.work_types?.[0]?.value || 'site_work';
  if (logType !== 'site_work') return null;
  const workPeriod = work.work_period || config.work_periods?.[0]?.value || 'in_hours';

  const journeyPart = valueOf(`journey:${me}`);
  const journey = journeyPart && journeyPart.travel_id
    ? (Array.isArray(saved.journeys) ? saved.journeys : []).find(row => lowerId(row.travel_id) === lowerId(journeyPart.travel_id))
    : null;
  if (!journey || journey.linked || !journey.departed_site_at || (journey.returned_at && !arrivedHere)) return null;
  const journeyDay = ukKeyOf(journey.departed_at);
  if (!journeyDay || journeyDay < ukTodayKey(now)) return null;

  if (valueOf('form:task')) return null;
  const robotKeys = Object.keys(parts).filter(key => key.startsWith('robot:') && valueOf(key));
  const robotsTotal = Number(saved.robots && saved.robots.total) || 0;
  if (robotsTotal === 0 || Number(saved.robots.done) !== robotsTotal || robotKeys.length !== robotsTotal) return null;
  const sections = [];
  for (const key of robotKeys) {
    const value = valueOf(key);
    const form = value.form || null;
    if (value.outcome === 'not_done') {
      if (!String(value.note || '').trim()) return null;
    } else if (value.outcome !== 'done' || !form || form.completed !== true || !form.template_id) {
      return null;
    }
    if (form && form.log_type && form.log_type !== logType) return null;
    const assetId = key.slice('robot:'.length);
    const attachments = filesOf(key).map(file => attachmentFromFileRef(file, file.step_key || ''));
    const answers = (form && form.answers) || {};
    sections.push({
      isRobot: true,
      assetId,
      useCheckTemplate: Boolean(form && form.template_id),
      hasContent: Object.keys(answers).length > 0 || attachments.length > 0,
      robotOutcome: {
        outcome: value.outcome,
        note: value.note || '',
        health_score: value.health_score || '',
        follow_up: value.follow_up || null
      },
      buildTemplateCheckEntry: () => ({
        template_id: form && form.template_id,
        asset_id: assetId,
        template_answers: answers,
        attachments,
        check_completed: Boolean(form && form.completed)
      })
    });
  }

  // Parts used (spare parts, spec 6.5): the server files the draft's lines
  // with this save and refuses one it cannot file (no robot on a job with
  // several, a robot taken off the job, a bad quantity). The tap never files
  // what would be refused; the job page says what to fix.
  const robotIds = new Set(robotKeys.map(key => key.slice('robot:'.length)));
  const spareValues = Object.entries(parts)
    .filter(([key, entry]) => key.startsWith('spare:') && entry && entry.value)
    .map(([, entry]) => entry.value);
  if (spareValues.some(value => spareValueProblem(value, { robotIds, severalRobots: robotsTotal > 1 }))) return null;

  const crew = valueOf('crew');
  if (!crew && !soloBooking) return null;
  const crewIds = crew && Array.isArray(crew.engineer_ids)
    ? [...new Set(crew.engineer_ids.map(lowerId))].filter(id => id && id !== me)
    : [];

  const van = valueOf('van_check');
  if (!van) return null;
  const vanCheck = {
    ...emptyDailyVanCheck,
    ...van,
    damage_photo_attachments: filesOf('van_check').map(file => attachmentFromFileRef(file))
  };
  if (validateDailyVanCheck(vanCheck)) return null;
  const onVisit = new Set([me, ...crewIds]);
  if ((vanCheck.seat_selections || []).filter(Boolean).some(id => !onVisit.has(lowerId(id)))) return null;

  const second = valueOf('second_van');
  let secondVan = null;
  let secondVanCheck = { ...emptyDailyVanCheck };
  const secondVanCheckOpen = Boolean(second && second.check_open);
  if (second) {
    if (!second.vehicle_id || !second.driver_id) return null;
    secondVan = { vehicle_id: second.vehicle_id, driver_id: second.driver_id };
    const { driver_id: driverId, check_open: checkOpen, ...check } = second;
    secondVanCheck = {
      ...emptyDailyVanCheck,
      ...check,
      damage_photo_attachments: filesOf('second_van').map(file => attachmentFromFileRef(file))
    };
    if (secondVanCheckOpen && validateDailyVanCheck(secondVanCheck)) return null;
  }

  // Someone else chose "back to me": the window shows it unset.
  const takenByOther = outcome.assign_next_type === 'self' && outcome.self_user_id && lowerId(outcome.self_user_id) !== me;
  const notes = valueOf('notes');
  const formData = {
    log_type: logType,
    started_at: '',
    ended_at: '',
    work_period: workPeriod,
    notes: notes && typeof notes.notes === 'string' ? notes.notes : '',
    outcome: outcome.outcome,
    attachments: [],
    assign_next_type: takenByOther ? '' : (outcome.assign_next_type || ''),
    assigned_to_next_id: outcome.assigned_to_next_id || '',
    completion_summary: outcome.completion_summary || '',
    follow_up_ticket_required: Boolean(outcome.follow_up_ticket_required),
    follow_up_subject: outcome.follow_up_subject || '',
    follow_up_description: outcome.follow_up_description || '',
    follow_up_priority: outcome.follow_up_priority || '',
    follow_up_asset_id: outcome.follow_up_asset_id || (sections[0] ? sections[0].assetId : '')
  };

  return {
    formData,
    sections,
    followUpPerRobot: robotsTotal > 1,
    journey,
    crewIds,
    vanCheck,
    secondVan,
    secondVanCheckOpen,
    secondVanCheck,
    workPeriod,
    robotsTotal
  };
}

/**
 * One job's "Ready to close" row. It holds the job's saved Log Work through
 * the engine (useSavedLogWork) and shows itself only while planJobClose says
 * it is ready. The tap stamps Arrived home on my Job Stages record, then
 * saves exactly as the Log Work window does (spec 6.4): flush, refresh, stop
 * on any colleague's change, then POST with the versions and files.
 */
function JobReadyToClose({ row, authFetch, user, config, soloBooking, onReadyChange, onClosed }) {
  const userId = user?.userId || null;
  const taskId = row.task_id;
  const saved = useSavedLogWork({
    authFetch,
    taskId,
    userId,
    enabled: Boolean(taskId && userId),
    userName: user?.fullName || user?.full_name || null
  });
  const savedRef = useRef(saved);
  savedRef.current = saved;
  const stampedRef = useRef(false);
  const [stamped, setStamped] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [homeTime, setHomeTime] = useState(null);

  // Wait for one render, so the engine's refreshed view is read (as the window does).
  const [, forceRender] = useState(0);
  const renderWaiters = useRef([]);
  useEffect(() => {
    if (renderWaiters.current.length === 0) return;
    const waiters = renderWaiters.current;
    renderWaiters.current = [];
    waiters.forEach(resolve => resolve());
  });
  const afterRender = () => new Promise(resolve => {
    renderWaiters.current.push(resolve);
    forceRender(count => count + 1);
  });

  // A stamp sent with no reply may have landed: the row stays (a later read
  // of the saved Log Work shows Arrived home set) and the next tap checks.
  const stampTriedRef = useRef(false);
  const [stampTried, setStampTried] = useState(false);
  const options = { userId, config, soloBooking, arrivedHere: stamped || stampTried };
  const plan = planJobClose(saved, options);
  const ready = Boolean(plan);
  useEffect(() => { onReadyChange(taskId, ready); }, [taskId, ready, onReadyChange]);
  useEffect(() => () => onReadyChange(taskId, false), [taskId, onReadyChange]);

  const closeJob = async (typedAt = null) => {
    setBusy(true);
    setError(null);
    try {
      const first = planJobClose(savedRef.current, { ...options, arrivedHere: stampedRef.current || stampTriedRef.current });
      if (!first) throw new Error(JOB_CLOSE_OPEN_MESSAGE);
      if (!stampedRef.current) {
        // Read my record first: a stamp whose reply was lost has already
        // landed, and stamping again would move Arrived home later (door to
        // door is paid). With no answer, nothing is stamped.
        const landed = await arrivalAlreadyRecorded(authFetch, first.journey.travel_id);
        if (landed) {
          stampedRef.current = true;
          setStamped(true);
          setHomeTime(null);
        }
      }
      if (!stampedRef.current) {
        const location = typedAt ? null : await getDeviceLocation();
        let stampResponse;
        try {
          stampResponse = await authFetch('/api/v1/travel/stages', {
            method: 'POST',
            body: JSON.stringify({
              stage: 'returned',
              task_id: taskId,
              travel_record_id: first.journey.travel_id,
              at: typedAt || undefined,
              lat: location?.lat,
              lng: location?.lng,
              accuracy: location?.accuracy
            })
          });
        } catch (err) {
          stampTriedRef.current = true;
          setStampTried(true);
          throw new Error(ARRIVED_HOME_LOST_MESSAGE);
        }
        const stampBody = await stampResponse.json().catch(() => ({}));
        if (!stampResponse.ok) {
          if (stampResponse.status === 409 && stampBody.code === 'STAMP_TIME_NEEDED') {
            // Nothing was stamped: ask for the time, and save only after it.
            setHomeTime(prev => ({ message: stampBody.message || '', value: prev?.value || '' }));
            return;
          }
          throw new Error(stampBody.message || `Could not record ${ARRIVED_HOME_LABEL}`);
        }
        stampedRef.current = true;
        setStamped(true);
        setHomeTime(null);
      }

      const current = savedRef.current;
      const flushed = await current.flush();
      if (flushed.unavailable) throw new Error(JOB_CLOSE_OPEN_MESSAGE);
      if (!flushed.ok) throw new Error(flushed.message || SEND_FAILED_MESSAGE);
      const refreshed = await current.refresh();
      if (refreshed.offline) throw new Error(NO_SIGNAL_SAVE_MESSAGE);
      if (Array.isArray(refreshed.changed) && refreshed.changed.length > 0) {
        throw new Error(refreshed.message || 'A colleague changed this Log Work. Check it, then save again.');
      }
      if (!refreshed.ok) throw new Error(refreshed.message || 'Log Work could not be checked. Try again.');
      await afterRender();
      const latest = savedRef.current;
      const final = planJobClose(latest, { ...options, arrivedHere: true });
      if (!final) throw new Error(JOB_CLOSE_CHANGED_MESSAGE);

      const body = buildLogWorkPayload({
        formData: { ...final.formData, attachments: latest.attachmentsFor('attachments') },
        sections: final.sections,
        closesWork: true,
        followUpPerRobot: final.followUpPerRobot,
        isSiteWork: true,
        dailyVanCheck: final.vanCheck,
        travelRecord: final.journey,
        additionalEngineerIds: final.crewIds,
        needsSecondVan: Boolean(final.secondVan),
        secondVan: final.secondVan || { vehicle_id: '', driver_id: '' },
        secondVanCheckOpen: final.secondVanCheckOpen,
        secondVanCheck: final.secondVanCheck,
        draftId: latest.draftId,
        carryOn: false,
        versions: latest.versionsForFiling(),
        fileIds: latest.fileIdsForFiling()
      });
      const response = await authFetch(`/api/v1/tasks/${taskId}/work-log`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        // The saved Log Work moved on: the view is refreshed and the phone copy kept.
        if (String(data.code || '').startsWith('LOG_WORK_')) {
          try { await latest.refresh(); } catch (err) { /* the sentence still shows */ }
        }
        throw new Error(data.message || 'Failed to save work log');
      }
      try { await latest.markFiled(); } catch (err) { /* found stale next time, never filed twice */ }
      const notices = [data.payroll_notice || null, data.already_saved ? data.message || null : null].filter(Boolean);
      await onClosed(notices.length > 0 ? notices.join(' ') : null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!ready && !busy && !error) return null;
  const journey = plan ? plan.journey : null;
  const periodLabel = plan
    ? ((config?.work_periods || []).find(period => period.value === plan.workPeriod)?.label || plan.workPeriod.replace(/_/g, ' '))
    : null;
  const timeBoxId = `today-job-home-time-${taskId}`;
  const name = [row.task_ref, row.customer_name].filter(Boolean).join(' · ') || 'Job';
  return (
    <div className="today-job-close">
      <div className="today-item">
        <div className="today-item-body">
          <strong>{name}</strong>
          <span>
            {row.title || 'Log Work'}
            {journey?.departed_site_at ? ` · left site ${formatTime(journey.departed_site_at)}` : ''}
            {periodLabel ? ` · ${periodLabel}` : ''}
            {stamped ? ' · arrival already recorded' : ''}
          </span>
          {plan && (
            <span>Log Work is filled in and closes the job ({plan.robotsTotal === 1 ? '1 robot' : `${plan.robotsTotal} robots`} done).</span>
          )}
        </div>
        {ready ? (
          <button
            type="button"
            className="today-chip today-arrive-home"
            disabled={busy}
            onClick={() => closeJob()}
          >
            {busy ? 'Closing…' : (stamped ? 'Save and close' : ARRIVED_HOME_LABEL)}
          </button>
        ) : (
          <Link className="today-chip today-log-work-chip" to={`/tasks/${taskId}?logwork=1`}>Open</Link>
        )}
      </div>
      {error && <div className="today-parked-error">{error}</div>}
      {homeTime && ready && (
        <div className="today-home-time" role="group" aria-label={`${ARRIVED_HOME_LABEL} time for ${row.task_ref || 'this job'}`}>
          {homeTime.message && <p className="today-home-time-message">{homeTime.message}</p>}
          <label htmlFor={timeBoxId}>What time did you get home, or change job?</label>
          <div className="today-home-time-row">
            <input
              id={timeBoxId}
              type="datetime-local"
              value={homeTime.value}
              onChange={event => {
                const value = event.target.value;
                setHomeTime(prev => ({ ...(prev || {}), value }));
              }}
            />
            <button
              type="button"
              className="today-chip today-arrive-home"
              disabled={!homeTime.value || busy}
              onClick={() => {
                const typed = new Date(homeTime.value);
                if (Number.isNaN(typed.getTime())) return;
                closeJob(typed.toISOString());
              }}
            >
              {busy ? 'Closing…' : 'Save and close'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// My Pay months are UK pay months (ONCALL_PAY_FIXES_DESIGN §8, commit B2).
// The browser's own calendar is right only for a browser in the UK - an
// engineer abroad saw a month the payroll does not pay by.
function monthRange(offset) {
  const [year, month] = ukTodayKey().split('-').map(Number);
  const first = new Date(year, month - 1 + offset, 1);
  const last = new Date(year, month + offset, 0);
  return {
    start: toDateKey(first),
    end: toDateKey(last),
    label: first.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
  };
}

/**
 * RR2: the engineer's own pay report — shown only when a super admin has
 * switched on the per-user grant (capabilities.view_own_pay). Their own
 * on-call standby, call-out and overtime figures; nobody else's.
 */
function MyPayCard({ authFetch }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [pay, setPay] = useState(null);
  const [payError, setPayError] = useState(null);
  const range = monthRange(monthOffset);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch(`/api/v1/on-call/payroll/me?start=${range.start}&end=${range.end}`);
        const body = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok) throw new Error(body.message || 'Failed to load pay report');
        setPay(body);
        setPayError(null);
      } catch (err) {
        if (!cancelled) { setPay(null); setPayError(err.message); }
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, range.start, range.end]);

  const gbp = (value) => `£${Number(value || 0).toFixed(2)}`;
  // The split the API sends. A month finalised before B4 has no split stored,
  // so the whole row reads as on time - which is exactly what it was.
  const num = (value, fallback) => (value === undefined || value === null ? Number(fallback || 0) : Number(value));
  const lateCount = (row) => num(row.late_incident_count, 0);
  const lateFees = (row) => num(row.late_incident_payment_gbp, 0);
  const lateHours = (row) => num(row.late_overtime_hours, 0);
  const lateOvertime = (row) => num(row.late_estimated_overtime_gbp, 0);
  const onTimeCount = (row) => num(row.on_time_incident_count, row.incident_count);
  const onTimeFees = (row) => num(row.on_time_incident_payment_gbp, row.incident_payment_gbp);
  const onTimeHours = (row) => num(row.on_time_overtime_hours, row.overtime_hours);
  const onTimeOvertime = (row) => num(row.on_time_estimated_overtime_gbp, row.estimated_overtime_gbp);
  const lateNights = [...new Set((pay?.late_lines || []).map((line) => line.night_day_label || line.night))];
  const standbyDays = (pay?.standby || []).reduce((sum, row) => sum + row.assigned_days, 0);
  const incidents = (pay?.call_outs || []).reduce((sum, row) => sum + row.incident_count, 0);
  const otHours = (pay?.call_outs || []).reduce((sum, row) => sum + row.overtime_hours, 0);

  return (
    <div className="today-section">
      <div className="today-pay-header">
        <h2>My Pay (on-call)</h2>
        <div className="today-pay-nav">
          <button type="button" onClick={() => setMonthOffset(monthOffset - 1)}>‹</button>
          <span>{range.label}</span>
          <button type="button" onClick={() => setMonthOffset(Math.min(0, monthOffset + 1))} disabled={monthOffset === 0}>›</button>
        </div>
      </div>
      {payError ? (
        <div className="today-empty">{payError}</div>
      ) : !pay ? (
        <div className="today-empty">Loading…</div>
      ) : (
        <>
          <div className="today-pay-stats">
            <div><span>{standbyDays}</span><label>Standby days</label><em>{gbp(pay.total_standby_gbp)}</em></div>
            <div><span>{incidents}</span><label>Call-outs</label><em>{gbp(pay.total_incident_gbp)}</em></div>
            <div><span>{otHours.toFixed(1)}h</span><label>Overtime (est.)</label><em>{gbp(pay.total_estimated_overtime_gbp)}</em></div>
            <div className="today-pay-total"><span>{gbp(pay.total_gbp)}</span><label>Period total</label></div>
          </div>
          {/* The workings behind each figure, line by line, so the numbers
              can be checked by hand (Gareth 2026-08-04). B4 splits call-outs
              into the ones from this month and the ones carried in late from a
              month that was already paid — the two lines add up to the figure
              above them, they are not extra to it (US2). */}
          <div className="today-pay-calcs">
            {(pay.standby || []).map((row, index) => (
              <span key={`standby-${index}`}>
                {row.cover_type_label} ({row.day_type === 'weekday' ? 'weekday' : row.day_type === 'weekend' ? 'weekend' : 'special day'}):{' '}
                {row.assigned_days} × {gbp(row.standby_rate_gbp)} = {gbp(row.standby_payment_gbp)}
              </span>
            ))}
            {(pay.call_outs || []).map((row, index) => (
              <React.Fragment key={`callout-${index}`}>
                {onTimeCount(row) > 0 && (
                  <span>
                    Call-outs on time: {onTimeCount(row)} × {gbp(onTimeFees(row) / onTimeCount(row))} = {gbp(onTimeFees(row))}
                  </span>
                )}
                {lateCount(row) > 0 && (
                  <span>
                    Late call-outs: {lateCount(row)} × {gbp(lateFees(row) / lateCount(row))} = {gbp(lateFees(row))}
                    {lateNights.length > 0 && ` (${nightsPhrase(lateNights)})`}
                  </span>
                )}
                {row.incident_count > 0 && <span>Call-out total {gbp(row.incident_payment_gbp)}</span>}
                {onTimeHours(row) > 0 && (
                  <span>
                    Overtime on time: {onTimeHours(row).toFixed(2)}h out-of-hours × your salaried hourly rate × {Number(pay.overtime_multiplier ?? 1.5)} = {gbp(onTimeOvertime(row))}
                  </span>
                )}
                {lateHours(row) > 0 && (
                  <span>Late overtime: {lateHours(row).toFixed(2)}h = {gbp(lateOvertime(row))}</span>
                )}
                {row.overtime_hours > 0 && <span>Overtime total {gbp(row.estimated_overtime_gbp)}</span>}
              </React.Fragment>
            ))}
            {(pay.skipped_fees || []).map((skip, index) => (
              <span key={`skipped-${index}`}>{skip.note}</span>
            ))}
            {(pay.times_changed_after_payment || []).map((changed, index) => (
              <span key={`changed-${index}`}>{changed.note}</span>
            ))}
            <span>Period total: {gbp(pay.total_standby_gbp)} + {gbp(pay.total_incident_gbp)} + {gbp(pay.total_estimated_overtime_gbp)} = {gbp(pay.total_gbp)}</span>
          </div>
          {pay.banner && <p className="today-hint">{pay.banner}</p>}
          {(pay.late_lines || []).length > 0 && (
            <p className="today-hint">
              Late items are call-outs from a pay period that was already finalised. They are paid in the
              next open period, shown here only when this is that period.
            </p>
          )}
          <p className="today-hint">
            {pay.mode === 'finalised'
              ? 'These are the finalised payroll figures for this period.'
              : 'Estimated from the rota and your work logs — the finalised payroll figure can differ.'}
          </p>
        </>
      )}
    </div>
  );
}

function Today() {
  const { authFetch, user } = useAuth();
  const showMyPay = !!getCapabilities(user).view_own_pay;
  // Spare parts (spec 6.6): the parts list is fetched here in the morning,
  // with signal, so the Log Work picker has it in a parlour with none.
  useSparePartsList({ authFetch, enabled: Boolean(user && canFitParts(user.role)) });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchToday = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/engineers/me/today');
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to load your day');
      }
      setData(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchToday();
    const timer = setInterval(fetchToday, 60000);
    return () => clearInterval(timer);
  }, [fetchToday]);

  // Arrived home one-tap close (2026-08-21). The work was written up on site
  // and parked; this stamps the drive home and replays the parked payload
  // through the ordinary work-log completion route, so ended_at becomes
  // door-to-door. Two calls, because the stamp has to exist before the close
  // derives its times from it — but only ever one tap.
  const [closingLogId, setClosingLogId] = useState(null);
  const [closeError, setCloseError] = useState(null);
  const [closeNotice, setCloseNotice] = useState(null);
  // Q4 (Phase 6 step 7): a live Arrived home more than 14 hours after the
  // stage before it is refused (409 STAMP_TIME_NEEDED). The engineer then
  // types the time here: { [log_id]: { message, value } }.
  const [homeTimes, setHomeTimes] = useState({});

  const arriveHomeAndClose = useCallback(async (parked, typedAt = null) => {
    setClosingLogId(parked.log_id);
    setCloseError(null);
    setCloseNotice(null);
    try {
      // Skipped when the stamp already landed, so a retry after a failed close
      // never overwrites the arrival with a later time.
      if (!parked.arrived_home_at) {
        // A typed time carries no location, as on the Job Stages card.
        const location = typedAt ? null : await getDeviceLocation();
        const stamped = await authFetch('/api/v1/travel/stages', {
          method: 'POST',
          body: JSON.stringify({
            stage: 'returned',
            ticket_id: parked.ticket_id,
            travel_record_id: parked.travel_id || undefined,
            at: typedAt || undefined,
            lat: location?.lat,
            lng: location?.lng,
            accuracy: location?.accuracy
          })
        });
        if (!stamped.ok) {
          const body = await stamped.json().catch(() => ({}));
          if (stamped.status === 409 && body.code === 'STAMP_TIME_NEEDED') {
            // Nothing was stamped: ask for the time, and close only after it.
            setHomeTimes(prev => ({
              ...prev,
              [parked.log_id]: { message: body.message || '', value: prev[parked.log_id]?.value || '' }
            }));
            return;
          }
          throw new Error(body.message || `Could not record ${ARRIVED_HOME_LABEL}`);
        }
      }

      const closed = await authFetch(
        `/api/v1/tickets/${parked.ticket_id}/work-log/${parked.log_id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ ...parked.pending_close, travel_record_id: parked.travel_id || undefined })
        }
      );
      const closedBody = await closed.json().catch(() => ({}));
      if (!closed.ok) {
        throw new Error(closedBody.message || `${ARRIVED_HOME_LABEL} was recorded, but the ticket could not be closed`);
      }
      // B4 (US4): closing a call-out whose month has already been paid tells
      // the engineer where the money goes. This response used to be thrown
      // away, so the notice never reached the person it was written for.
      setCloseNotice(closedBody.payroll_notice || null);
      setHomeTimes(prev => {
        if (!prev[parked.log_id]) return prev;
        const next = { ...prev };
        delete next[parked.log_id];
        return next;
      });

      await fetchToday();
    } catch (err) {
      setCloseError(err.message);
      await fetchToday();
    } finally {
      setClosingLogId(null);
    }
  }, [authFetch, fetchToday]);

  // Last heartbeat outcome (written by the Layout heartbeat) — surfaces
  // silent location failures where the engineer actually looks.
  const [locationStatus, setLocationStatus] = useState(readLocationStatus);
  useEffect(() => {
    const timer = setInterval(() => setLocationStatus(readLocationStatus()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Read-only absence register (leave itself lives in Breathe)
  const [absences, setAbsences] = useState([]);

  // Section 5 item 3: my own week at a glance
  const [week, setWeek] = useState([]);

  const fetchWeek = useCallback(async () => {
    try {
      const weekStart = toDateKey(startOfWeek(new Date()));
      const response = await authFetch(`/api/v1/engineers/me/week?week_start=${weekStart}`);
      if (response.ok) {
        const body = await response.json();
        setWeek(body.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch week:', err);
    }
  }, [authFetch]);

  useEffect(() => { fetchWeek(); }, [fetchWeek]);

  const fetchAbsences = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/engineers/me/absences');
      if (response.ok) {
        const body = await response.json();
        setAbsences(body.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch absences:', err);
    }
  }, [authFetch]);

  useEffect(() => { fetchAbsences(); }, [fetchAbsences]);

  // Log Work changes still on this phone (Phase 6 step 7). Read again each
  // time the day reloads, so a change sent from the job page drops off.
  const [phoneUnsent, setPhoneUnsent] = useState([]);
  const userId = user?.userId || null;
  const serverToFinish = data?.log_work_to_finish;
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const activeDrafts = {};
        (Array.isArray(serverToFinish) ? serverToFinish : []).forEach(row => {
          if (row && row.task_id && row.draft_id) activeDrafts[String(row.task_id)] = row.draft_id;
        });
        const listed = await getLogWorkPhoneStore().listTasksWithUnsent(userId, { activeDrafts });
        if (!cancelled) setPhoneUnsent(Array.isArray(listed) ? listed : []);
      } catch (err) {
        // The phone's copy is a convenience here; the job page still has it.
        if (!cancelled) setPhoneUnsent([]);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, serverToFinish]);

  // 7c: a job whose saved Log Work closes it and waits only for the drive
  // home. The candidates are cheap to pick from /today; each one then reads
  // its saved Log Work, and shows a row only when it really is ready.
  const jobCandidates = useMemo(() => jobCloseCandidates(serverToFinish), [serverToFinish]);
  const hasJobCandidates = jobCandidates.length > 0;
  const [workLogConfig, setWorkLogConfig] = useState(null);
  useEffect(() => {
    if (!hasJobCandidates || workLogConfig) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await authFetch('/api/v1/support/work-log-config');
        if (!response.ok) return;
        const body = await response.json();
        if (!cancelled && body && typeof body === 'object') setWorkLogConfig(body);
      } catch (err) {
        // Without the config no job row is offered; the job page still works.
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch, hasJobCandidates, workLogConfig]);
  const [readyJobIds, setReadyJobIds] = useState([]);
  const onJobReadyChange = useCallback((taskId, ready) => {
    setReadyJobIds(prev => {
      const has = prev.includes(taskId);
      if (ready === has) return prev;
      return ready ? [...prev, taskId] : prev.filter(id => id !== taskId);
    });
  }, []);
  const onJobClosed = useCallback(async (notice) => {
    setCloseNotice(notice || null);
    await fetchToday();
  }, [fetchToday]);

  if (loading) return <div className="today-page"><div className="loading">Loading your day...</div></div>;
  if (error) return <div className="today-page"><div className="today-error">{error}</div></div>;

  const unloggedMs = Math.max(0, data.clock.clocked_ms_today - data.clock.logged_ms_today);
  const logWorkRows = mergeLogWorkToFinish(data.log_work_to_finish, phoneUnsent, data.tasks_today);

  return (
    <div className="today-page">
      <div className="today-header">
        <div>
          <h1>Today</h1>
          <p>{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} · {data.name}</p>
        </div>
        <Link className="today-profile-link" to="/admin?tab=profile">My Profile</Link>
      </div>

      {data.absence && (
        <div className="today-absence">
          You are recorded as on {data.absence.absence_type.replace(/_/g, ' ')} today
          ({formatDateKey(data.absence.starts_at)} – {formatDateKey(data.absence.ends_at)}).
        </div>
      )}

      {data.on_call_today.length > 0 && (
        <div className="today-standby">
          📞 On standby today: {data.on_call_today.join(', ')}
        </div>
      )}

      {/* B4 (US4): where the money for a just-closed call-out goes. It lives
          OUTSIDE the 'Ready to close' section on purpose. Closing the job
          removes it from parked_closes, so on the ordinary run - one parked job,
          now closed - that section unmounts on the refresh and would take this
          sentence with it before the engineer could read it. */}
      {closeNotice && (
        <div className="today-section today-pay-notice" role="status">
          {closeNotice}
        </div>
      )}

      {/* Work written up on site, waiting only for the drive home. One tap
          stamps the arrival and closes it, so the drive is paid without
          reopening the ticket. */}
      {/* 7c: a job's saved Log Work that closes it joins this section. Each
          candidate stays mounted (it holds its saved Log Work) and shows a
          row only once it is ready, so the heading waits for one. */}
      {(data.parked_closes?.length > 0 || jobCandidates.length > 0) && (
        <div className={(data.parked_closes?.length > 0 || readyJobIds.length > 0) ? 'today-section today-parked' : 'today-parked-pending'}>
          {(data.parked_closes?.length > 0 || readyJobIds.length > 0) && (
            <>
              <h2>🏠 Ready to close</h2>
              <p className="today-hint">
                Finished and written up — tap when you are home and the drive home counts towards the job.
              </p>
            </>
          )}
          {closeError && <div className="today-parked-error">{closeError}</div>}
          {(data.parked_closes || []).map(parked => {
            const homeTime = homeTimes[parked.log_id];
            const timeBoxId = `today-home-time-${parked.log_id}`;
            return (
              <React.Fragment key={parked.log_id}>
                <div className="today-item">
                  <div className="today-item-body">
                    <strong>{parked.ticket_ref} · {parked.customer_name}</strong>
                    <span>
                      {parked.subject}
                      {parked.finished_site_at
                        ? ` · left site ${new Date(parked.finished_site_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                        : ''}
                      {parked.arrived_home_at ? ' · arrival already recorded' : ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="today-chip today-arrive-home"
                    disabled={closingLogId === parked.log_id}
                    onClick={() => arriveHomeAndClose(parked)}
                  >
                    {closingLogId === parked.log_id ? 'Closing…' : ARRIVED_HOME_LABEL}
                  </button>
                </div>
                {homeTime && (
                  <div className="today-home-time" role="group" aria-label={`${ARRIVED_HOME_LABEL} time for ${parked.ticket_ref}`}>
                    {homeTime.message && <p className="today-home-time-message">{homeTime.message}</p>}
                    <label htmlFor={timeBoxId}>What time did you get home, or change job?</label>
                    <div className="today-home-time-row">
                      <input
                        id={timeBoxId}
                        type="datetime-local"
                        value={homeTime.value}
                        onChange={event => {
                          const value = event.target.value;
                          setHomeTimes(prev => ({ ...prev, [parked.log_id]: { ...prev[parked.log_id], value } }));
                        }}
                      />
                      <button
                        type="button"
                        className="today-chip today-arrive-home"
                        disabled={!homeTime.value || closingLogId === parked.log_id}
                        onClick={() => {
                          const typed = new Date(homeTime.value);
                          if (Number.isNaN(typed.getTime())) return;
                          arriveHomeAndClose(parked, typed.toISOString());
                        }}
                      >
                        {closingLogId === parked.log_id ? 'Closing…' : 'Save and close'}
                      </button>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {jobCandidates.map(row => {
            const booked = (data.tasks_today || []).find(task => String(task.task_id) === String(row.task_id));
            return (
              <JobReadyToClose
                key={row.task_id}
                row={row}
                authFetch={authFetch}
                user={user}
                config={workLogConfig}
                soloBooking={Boolean(booked) && !booked.crew_names}
                onReadyChange={onJobReadyChange}
                onClosed={onJobClosed}
              />
            );
          })}
        </div>
      )}

      {/* Phase 6 step 7: saved Log Work not yet saved as hours - the server's
          list for this engineer, plus any job with changes still on this
          phone. Nothing in these is paid until Complete Work Log. */}
      {logWorkRows.length > 0 && (
        <div className="today-section today-log-work">
          <h2>Log Work to finish ({logWorkRows.length})</h2>
          <p className="today-hint">
            Saved part-way, not hours yet. Hours count only once Log Work is completed with Complete Work Log.
          </p>
          {logWorkRows.map(row => {
            const detail = logWorkRowDetail(row);
            const phone = phoneLine(row.phone);
            const name = [row.task_ref || (row.phone_only ? 'Job' : null), row.customer_name].filter(Boolean).join(' · ');
            return (
              <Link key={row.task_id} to={`/tasks/${row.task_id}?logwork=1`} className="today-item">
                <div className="today-item-body">
                  <strong>{name || 'Job'}</strong>
                  {row.title && <span>{row.title}</span>}
                  {detail && <span>{detail}</span>}
                  {row.my_unfiled_journey && (
                    <span>Your Job Stages here are not in any hours yet.</span>
                  )}
                  {phone && <span className="today-log-work-phone">{phone}</span>}
                </div>
                <span className="today-chip today-log-work-chip">Continue</span>
              </Link>
            );
          })}
        </div>
      )}

      {/* Phase 4: entries a colleague logged for this engineer (a log each
          on a crewed job or ticket), still waiting for them to confirm or
          correct. Kept here after the job is complete, because an
          unconfirmed out-of-hours entry holds up its call-out pay. */}
      {data.logs_to_confirm?.length > 0 && (
        <div className="today-section today-hours-to-confirm">
          <h2>Hours to confirm ({data.logs_to_confirm.length})</h2>
          <p className="today-hint">
            A colleague logged you on these. Check the times and confirm them, or correct your finish.
          </p>
          {data.logs_to_confirm.map(entry => {
            const href = entry.task_id ? `/tasks/${entry.task_id}` : `/tickets/${entry.ticket_id}`;
            return (
              <Link key={entry.log_id} to={href} className="today-item">
                <div className="today-item-body">
                  <strong>{entry.ref}{entry.title ? ` · ${entry.title}` : ''}</strong>
                  <span>
                    {entry.customer_name || 'Unknown customer'}
                    {entry.lead_name ? ` · logged by ${entry.lead_name}` : ''}
                  </span>
                  <span>
                    {formatDateTime(entry.started_at)}
                    {' - '}
                    {!entry.ended_at
                      ? 'no finish yet'
                      : (formatDate(entry.ended_at) === formatDate(entry.started_at) ? formatTime(entry.ended_at) : formatDateTime(entry.ended_at))}
                  </span>
                </div>
                <span className="today-chip today-confirm-chip">Confirm</span>
              </Link>
            );
          })}
        </div>
      )}

      {/* Reopened work logs (Gareth 2026-10-13): my own completed logs that
          were reopened for me to update, kept here until I complete them
          again. */}
      {data.logs_to_update?.length > 0 && (
        <div className="today-section today-logs-to-update">
          <h2>Work logs to update ({data.logs_to_update.length})</h2>
          <p className="today-hint">
            These were reopened for you. Update each one, then complete it again.
          </p>
          {data.logs_to_update.map(entry => (
            <Link key={entry.reopen_id} to={reopenedWorkHref(entry)} className="today-item">
              <div className="today-item-body">
                <strong>{entry.ref}{entry.title ? ` · ${entry.title}` : ''}</strong>
                <span>
                  {entry.customer_name || 'Unknown customer'}
                  {entry.reopened_by_name ? ` · reopened by ${entry.reopened_by_name}` : ''}
                  {entry.reopened_at ? ` · ${formatDateTime(entry.reopened_at)}` : ''}
                </span>
                {entry.reopen_reason && <span>Reason: {entry.reopen_reason}</span>}
              </div>
              <span className="today-chip today-confirm-chip">Update</span>
            </Link>
          ))}
        </div>
      )}

      {/* For a team leader, manager or admin: engineers asking for a
          completed work log to be reopened. Reopened or declined on the
          ticket's Work Logs tab. */}
      {data.reopen_requests?.length > 0 && (
        <div className="today-section today-reopen-requests">
          <h2>Reopen requests ({data.reopen_requests.length})</h2>
          <p className="today-hint">
            An engineer has asked for a completed work log to be reopened. Open it to reopen or decline.
          </p>
          {data.reopen_requests.map(entry => (
            <Link key={entry.reopen_id} to={reopenedWorkHref(entry)} className="today-item">
              <div className="today-item-body">
                <strong>{entry.ref}{entry.title ? ` · ${entry.title}` : ''}</strong>
                <span>
                  {entry.customer_name || 'Unknown customer'}
                  {entry.requested_by_name ? ` · asked by ${entry.requested_by_name}` : ''}
                  {entry.requested_at ? ` · ${formatDateTime(entry.requested_at)}` : ''}
                </span>
                {entry.request_reason && <span>Reason: {entry.request_reason}</span>}
              </div>
              <span className="today-chip today-confirm-chip">Decide</span>
            </Link>
          ))}
        </div>
      )}

      {/* Shown while driving with a fresh location ping: the nearest vetted
          places to stop for the night instead of driving on too long. */}
      {data.nearby_accommodation?.items?.length > 0 && (
        <div className="today-section today-accommodation">
          <h2>🛏 Nearest places to stay</h2>
          <p className="today-hint">
            Based on your location at {new Date(data.nearby_accommodation.as_of).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}.
            Better a bed than a long drive.
          </p>
          {data.nearby_accommodation.items.map(place => (
            <div key={place.accommodation_id} className="today-item">
              <div className="today-item-body">
                <strong>{place.name}</strong>
                <span>
                  {place.distance_miles} mi
                  {place.town_city ? ` · ${place.town_city}` : ''}
                  {place.avg_rating ? ` · ${place.avg_rating}/10 (${place.stay_count})` : ' · not yet rated'}
                  {place.typical_price_per_night ? ` · ~£${Number(place.typical_price_per_night).toFixed(0)}/night` : ''}
                </span>
              </div>
              {place.phone && (
                <a className="today-chip today-call" href={`tel:${place.phone}`}>Call</a>
              )}
              <CoordsCopy lat={place.lat} lng={place.lng} compact />
            </div>
          ))}
          <Link to="/accommodation" className="today-accom-link">Open the full directory →</Link>
        </div>
      )}

      <div className="today-clock-card">
        <div className={`today-clock-state ${data.clock.clocked_in ? 'in' : 'out'}`}>
          {data.clock.clocked_in ? 'Clocked in' : 'Clocked out'}
        </div>
        <div className="today-clock-stats">
          <div><span>{formatHours(data.clock.clocked_ms_today)}</span><label>Clocked</label></div>
          <div><span>{formatHours(data.clock.logged_ms_today)}</span><label>Logged ({data.clock.log_count_today})</label></div>
          <div className={unloggedMs > 2 * 3600000 ? 'gap-warn' : ''}>
            <span>{formatHours(unloggedMs)}</span><label>Not yet logged</label>
          </div>
        </div>
        {data.clock.clocked_in && locationStatusLabel(locationStatus) && (
          <div className={`today-location-status ${locationStatus?.state === 'sent' ? 'ok' : 'warn'}`}>
            {locationStatusLabel(locationStatus)}
          </div>
        )}
      </div>

      {week.length > 0 && (
        <div className="today-section">
          <h2>My Week</h2>
          <div className="today-week-strip">
            {week.map(day => (
              <div key={day.date} className={`today-week-day ${day.flags.length ? 'flagged' : ''}`}>
                <span className="week-day-name">{formatDateKey(day.date, { weekday: 'short' })}</span>
                <span className="week-day-hours">{formatHours(day.logged_ms)}</span>
                <span className="week-day-sub">of {formatHours(day.clocked_ms)}</span>
                {(day.flags || []).includes('unfinished_log_work') && (
                  <span className="week-day-flag">Log Work to finish</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showMyPay && <MyPayCard authFetch={authFetch} />}

      <div className="today-section">
        <h2>Today's Jobs ({data.tasks_today.length})</h2>
        {data.tasks_today.length === 0 ? (
          <div className="today-empty">Nothing scheduled for today.</div>
        ) : (
          data.tasks_today.map(task => (
            <Link key={task.task_id} to={`/tasks/${task.task_id}`} className="today-item">
              <div className="today-item-time">{task.scheduled_start_time ? task.scheduled_start_time.slice(0, 5) : '—'}</div>
              <div className="today-item-body">
                <strong>{task.title}</strong>
                <span>
                  {task.customer_name}
                  {task.asset_ref ? ` · ${task.asset_ref}` : ''}
                  {task.asset_ref && Number(task.asset_count) > 1 ? ` +${Number(task.asset_count) - 1}` : ''}
                  {/* Several engineers on one job (Phase 5): who else is on it. */}
                  {task.crew_names ? ` · with ${task.crew_names}` : ''}
                </span>
              </div>
              <span className={`today-chip status-${task.status}`}>{task.status.replace(/_/g, ' ')}</span>
            </Link>
          ))
        )}
      </div>

      <div className="today-section">
        <h2>My Open Tickets ({data.open_tickets.length})</h2>
        {data.open_tickets.length === 0 ? (
          <div className="today-empty">No open tickets assigned to you.</div>
        ) : (
          data.open_tickets.map(ticket => (
            <Link key={ticket.ticket_id} to={`/tickets/${ticket.ticket_id}`} className="today-item">
              <div className={`today-priority ${ticket.priority}`}>{ticket.priority}</div>
              <div className="today-item-body">
                <strong>{ticket.subject}</strong>
                <span>{ticket.ticket_ref} · {ticket.customer_name}{ticket.asset_ref ? ` · ${ticket.asset_ref}` : ''}</span>
              </div>
              {ticket.sla_resolution_due && new Date(ticket.sla_resolution_due) < new Date() && (
                <span className="today-chip sla-breach">SLA</span>
              )}
            </Link>
          ))
        )}
      </div>

      <div className="today-section">
        <div className="today-leave-header">
          <h2>My Leave</h2>
        </div>
        <p className="today-hint">
          Leave is requested and approved in Breathe. Absences recorded here keep the
          scheduling warnings accurate.
        </p>

        {absences.length === 0 ? (
          <div className="today-empty">No leave recorded.</div>
        ) : (
          absences.slice(0, 6).map(absence => (
            <div key={absence.absence_id} className="today-item">
              <div className="today-item-body">
                <strong>{absence.absence_type.replace(/_/g, ' ')}</strong>
                <span>
                  {formatDateKey(absence.starts_at)} – {formatDateKey(absence.ends_at)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Today;
